import { PathsV1UserSearchPostParametersQuerySearchField, UserStatus, UserType } from "@/api-schema-enums";
import { useDevice } from "@/common/hooks/use-device";
import { useNavigationItem } from "@/common/hooks/use-navigation";
import { PhonebookTable } from "@/components/data-table/predefined/phonebook-table";
import { DataTableHeightMode } from "@/components/data-table/types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { CompanyFilter } from "@/components/filter/predefined/company-filter";
import { DepartmentFilter } from "@/components/filter/predefined/department-filter";
import { JobFunctionFilter } from "@/components/filter/predefined/job-function-filter";
import { OccupationFilter } from "@/components/filter/predefined/occupation-filter";
import { UserStatusFilter } from "@/components/filter/predefined/user-status-filter";
import { UserTypeFilter } from "@/components/filter/predefined/user-type-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { NoPermissionCard } from "@/components/no-permission-card/no-permission-card";
import { userConfigStore } from "@/store/user-config-store";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PhonebookFilters } from "./_components/phonebook-filters";

const DEFAULT_PAGE_SIZE = 100;
const PHONEBOOK_SEARCH_FIELDS = Object.values(PathsV1UserSearchPostParametersQuerySearchField);

const DEFAULT_FILTER_VALUES = {
  departments: [],
  companies: [],
  branches: [],
  brands: [],
  occupations: [],
  jobFunctions: [],
  userTypes: [UserType.Employee],
  userStatuses: [UserStatus.Enabled],
};

export function PhonebookView() {
  const { t } = useTranslation(["phonebook", "common"]);
  const config = useNavigationItem("phonebook");
  const { isMobile } = useDevice();
  const { phonebookFiltersVisible } = userConfigStore();

  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(DEFAULT_FILTER_VALUES);

  const filters = useMemo(
    () => [
      new DepartmentFilter(),
      new CompanyFilter(),
      new BranchFilter(),
      new BrandFilter(),
      new OccupationFilter(),
      new JobFunctionFilter(),
      new UserTypeFilter(),
      new UserStatusFilter(),
    ],
    [],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterId]: values,
    }));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilterValues({
      ...DEFAULT_FILTER_VALUES,
      departments: [],
      companies: [],
      branches: [],
      brands: [],
      occupations: [],
      jobFunctions: [],
      userTypes: [],
      userStatuses: [],
    });
  }, []);

  const handleResetAllFilters = useCallback(() => {
    setFilterValues(DEFAULT_FILTER_VALUES);
  }, []);

  if (!config) {
    return <NoPermissionCard />;
  }

  const showDesktopFilterPanel = !isMobile && phonebookFiltersVisible;

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("phonebook:Phonebook")}
      subTitle={t("phonebook:PhonebookDescription")}
    >
      <div className="flex min-h-0 flex-1 flex-col space-y-4">
        {showDesktopFilterPanel && (
          <PhonebookFilters
            filters={filters}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            onResetAllFilters={handleResetAllFilters}
          />
        )}

        <PhonebookTable
          query={{
            searchField: PHONEBOOK_SEARCH_FIELDS,
          }}
          body={{
            ...(filterValues.departments.length > 0 && {
              departments: filterValues.departments,
            }),
            ...(filterValues.companies.length > 0 && {
              companies: filterValues.companies,
            }),
            ...(filterValues.branches.length > 0 && { mainBranch: filterValues.branches }),
            ...(filterValues.brands.length > 0 && { brands: filterValues.brands }),
            ...(filterValues.occupations.length > 0 && {
              occupations: filterValues.occupations,
            }),
            ...(filterValues.jobFunctions.length > 0 && {
              jobFunctions: filterValues.jobFunctions,
            }),
            ...(filterValues.userTypes.length > 0 && {
              type: filterValues.userTypes as UserType[],
            }),
            ...(filterValues.userStatuses.length > 0 && {
              status: filterValues.userStatuses as UserStatus[],
            }),
          }}
          pageSize={DEFAULT_PAGE_SIZE}
          filters={isMobile || !phonebookFiltersVisible ? filters : []}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          heightMode={DataTableHeightMode.Content}
        />
      </div>
    </CenteredLayout>
  );
}
